import React, { useEffect, useMemo, useState } from 'react'
import {
  ArrowLeft, Save, RotateCcw, Plus, Trash2, Bell, Send, Eye, ChevronLeft, ChevronRight, Download,
} from 'lucide-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import subscriptionService from '../../services/subscriptionService'
import fileUploadService from '../../services/fileUploadService'
import {
  BILLING_CYCLES,
  BILLING_MODELS,
  CRITICALITIES,
  DOCUMENT_TYPES,
  ENVIRONMENTS,
  PAYMENT_METHODS,
  REMINDER_TRIGGER_BASES,
  RENEWAL_DECISIONS,
  RESOURCE_TYPES,
  SUBSCRIPTION_CATEGORIES,
  SUBSCRIPTION_STATUSES,
  emptyForm,
  emptyReminder,
  emptyResource,
  mapSubscriptionToForm,
} from './subscriptionUtils'
import { exportSubscriptionResourcesExcel } from './exportSubscriptionResources'

const TABS = [
  { id: 'basic', label: 'Basic' },
  { id: 'period', label: 'Period' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'gst', label: 'GST & Accounting' },
  { id: 'resources', label: 'Resources' },
  { id: 'reminders', label: 'Reminders' },
  { id: 'renewal', label: 'Renewal & Usage' },
  { id: 'documents', label: 'Documents' },
]

const RESOURCES_PAGE_SIZE = 10

const Field = ({ label, children, className = 'col-md-4' }) => (
  <div className={className}>
    <label className="form-label">{label}</label>
    {children}
  </div>
)

const SubscriptionForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isView = location.pathname.includes('/view/')
  const isEdit = location.pathname.includes('/edit/')
  const isCreate = location.pathname.endsWith('/new')

  const [formData, setFormData] = useState(emptyForm())
  const [tab, setTab] = useState('basic')
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(isEdit || isView)
  const [reminderPreview, setReminderPreview] = useState(null)
  const [resourcePage, setResourcePage] = useState(1)
  const [exportResourceType, setExportResourceType] = useState('')
  const [exportingResources, setExportingResources] = useState(false)

  useEffect(() => {
    if ((isEdit || isView) && id) loadSubscription(id)
  }, [id, isEdit, isView])

  const resources = formData.resources || []
  const resourceTotalPages = Math.max(1, Math.ceil(resources.length / RESOURCES_PAGE_SIZE))

  useEffect(() => {
    setResourcePage((p) => Math.min(Math.max(1, p), resourceTotalPages))
  }, [resourceTotalPages, resources.length])

  const pagedResources = useMemo(() => {
    const start = (resourcePage - 1) * RESOURCES_PAGE_SIZE
    return resources
      .slice(start, start + RESOURCES_PAGE_SIZE)
      .map((r, i) => ({ resource: r, index: start + i }))
  }, [resources, resourcePage])

  const resourceRangeLabel = resources.length === 0
    ? '0 of 0'
    : `${(resourcePage - 1) * RESOURCES_PAGE_SIZE + 1}–${Math.min(resourcePage * RESOURCES_PAGE_SIZE, resources.length)} of ${resources.length}`

  const resourceTypesPresent = useMemo(() => {
    const set = new Set()
    resources.forEach((r) => {
      if (r.resourceType) set.add(r.resourceType)
    })
    return Array.from(set).sort()
  }, [resources])

  const handleExportResources = () => {
    try {
      setExportingResources(true)
      const result = exportSubscriptionResourcesExcel({
        subscription: formData,
        resources,
        resourceType: exportResourceType || '',
      })
      toast.success(`Exported ${result.count} resource(s) to ${result.filename}`)
    } catch (error) {
      toast.error(error.message || 'Failed to export resources')
    } finally {
      setExportingResources(false)
    }
  }

  const loadSubscription = async (subscriptionId) => {
    try {
      setInitialLoading(true)
      const response = await subscriptionService.getById(subscriptionId)
      if (response.success && response.data) {
        setFormData(mapSubscriptionToForm(response.data))
      } else {
        toast.error(response.message || 'Subscription not found')
        navigate('/subscriptions/list')
      }
    } catch (error) {
      if (!error._errorHandled) toast.error('Failed to load subscription')
      navigate('/subscriptions/list')
    } finally {
      setInitialLoading(false)
    }
  }

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value,
        },
      }))
      return
    }
    setField(name, type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value)
  }

  const updateResource = (index, patch) => {
    setFormData((prev) => {
      const resources = [...(prev.resources || [])]
      resources[index] = { ...resources[index], ...patch }
      return { ...prev, resources }
    })
  }

  const updateReminder = (index, patch) => {
    setFormData((prev) => {
      const reminders = [...(prev.reminders || [])]
      reminders[index] = { ...reminders[index], ...patch }
      return { ...prev, reminders }
    })
  }

  const buildPayload = () => {
    const f = formData
    return {
      subscriptionName: f.subscriptionName?.trim(),
      description: f.description,
      internalReference: f.internalReference,
      vendorAccountId: f.vendorAccountId,
      planName: f.planName,
      businessPurpose: f.businessPurpose,
      provider: f.vendorName || f.provider,
      vendorName: f.vendorName || f.provider,
      vendorLegalEntity: f.vendorLegalEntity,
      category: f.category,
      billingModel: f.billingModel,
      status: f.status,
      criticality: f.criticality,
      department: f.department,
      projectName: f.projectName,
      costCentre: f.costCentre,
      startDate: f.startDate,
      currentPeriodStart: f.currentPeriodStart || undefined,
      currentPeriodEnd: f.currentPeriodEnd || undefined,
      renewalDate: f.renewalDate || undefined,
      endDate: f.endDate || f.expiryDate || undefined,
      expiryDate: f.expiryDate || f.endDate || undefined,
      contractEndDate: f.contractEndDate || undefined,
      noticePeriodDays: Number(f.noticePeriodDays) || 0,
      noticeDeadline: f.noticeDeadline || undefined,
      minimumCommitmentEnd: f.minimumCommitmentEnd || undefined,
      billingCycle: f.billingCycle,
      autoRenewal: Boolean(f.autoRenewal),
      autoPayment: Boolean(f.autoPayment),
      gracePeriodDays: Number(f.gracePeriodDays) || 0,
      customBillingSchedule: f.customBillingSchedule,
      currency: f.currency,
      baseRecurringAmount: Number(f.baseRecurringAmount) || 0,
      expectedUsageAmount: Number(f.expectedUsageAmount) || 0,
      expectedTotalAmount: Number(f.expectedTotalAmount) || 0,
      lastBilledAmount: Number(f.lastBilledAmount) || 0,
      taxAmount: Number(f.taxAmount) || 0,
      totalAmount: Number(f.totalAmount) || 0,
      exchangeRate: Number(f.exchangeRate) || 1,
      inrEquivalent: Number(f.inrEquivalent) || 0,
      budgetedAnnualCost: Number(f.budgetedAnnualCost) || 0,
      actualYtdCost: Number(f.actualYtdCost) || 0,
      monthlyCost: Number(f.monthlyCost) || Number(f.baseRecurringAmount) || 0,
      annualCost: Number(f.annualCost) || 0,
      seats: Number(f.seats) || Number(f.purchasedLicences) || 0,
      purchasedLicences: Number(f.purchasedLicences) || Number(f.seats) || 0,
      assignedLicences: Number(f.assignedLicences) || 0,
      paymentMethod: f.paymentMethod,
      paymentInstrument: f.paymentInstrument,
      paymentTerms: f.paymentTerms,
      purchaseOrderNumber: f.purchaseOrderNumber,
      contractNumber: f.contractNumber,
      securityDeposit: Number(f.securityDeposit) || 0,
      refundableDeposit: Boolean(f.refundableDeposit),
      gst: f.gst,
      usageBudget: f.usageBudget,
      usageReview: f.usageReview,
      renewal: f.renewal,
      resources: f.resources,
      documents: f.documents,
      costAllocations: f.costAllocations,
      reminders: f.reminders,
      notes: f.notes,
      businessOwner: f.businessOwner || undefined,
      technicalOwner: f.technicalOwner || undefined,
      financeOwner: f.financeOwner || undefined,
      vendor: f.vendor || undefined,
      project: f.project || undefined,
    }
  }

  const handleSave = async () => {
    if (!formData.subscriptionName?.trim()) {
      toast.error('Subscription name is required')
      return
    }
    if (!formData.startDate) {
      toast.error('Start date is required')
      return
    }
    try {
      setLoading(true)
      const payload = buildPayload()
      const response = isEdit && id
        ? await subscriptionService.update(id, payload)
        : await subscriptionService.create(payload)
      if (response.success) {
        toast.success(isEdit ? 'Subscription updated' : 'Subscription created')
        const newId = response.data?._id || id
        navigate(newId ? `/subscriptions/view/${newId}` : '/subscriptions/list')
      } else {
        toast.error(response.message || 'Failed to save subscription')
      }
    } catch (error) {
      if (!error._errorHandled) toast.error('Failed to save subscription')
    } finally {
      setLoading(false)
    }
  }

  const runReminderAction = async (action) => {
    if (!id) {
      toast.info('Save the subscription before sending reminders')
      return
    }
    const reminderId = formData.reminders?.[0]?._id
    try {
      setLoading(true)
      let response
      if (action === 'preview') response = await subscriptionService.previewReminder(id, { reminderId })
      else if (action === 'test') response = await subscriptionService.sendTestReminder(id, { reminderId })
      else response = await subscriptionService.sendReminderNow(id, { reminderId })
      if (response.success) {
        if (action === 'preview') {
          setReminderPreview(response.data)
          toast.success('Preview generated')
        } else {
          toast.success(response.message || 'Reminder processed')
        }
      } else toast.error(response.message || 'Reminder action failed')
    } catch (error) {
      if (!error._errorHandled) toast.error('Reminder action failed')
    } finally {
      setLoading(false)
    }
  }

  const pageTitle = isView ? 'View Subscription' : isEdit ? 'Edit Subscription' : 'New Subscription'
  const disabled = isView || loading || initialLoading

  const breadcrumbItems = [
    { label: 'Subscriptions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
    { label: 'Subscriptions', path: '/subscriptions/list', onClick: () => navigate('/subscriptions/list') },
    { label: pageTitle, isCurrent: true },
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />
      <div className="page-header">
        <div className="page-title">
          <h1>{pageTitle}</h1>
          <p>Track service subscriptions, resources, renewals, GST and reminders</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} /> Back
          </button>
          {!isView && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => (isEdit ? loadSubscription(id) : setFormData(emptyForm()))} disabled={loading}>
                <RotateCcw size={16} /> Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={loading || initialLoading}>
                <Save size={16} /> {loading ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
          {isView && id && (
            <button type="button" className="btn-primary" onClick={() => navigate(`/subscriptions/edit/${id}`)}>
              Edit
            </button>
          )}
        </div>
      </div>

      {initialLoading ? (
        <p className="text-muted">Loading subscription…</p>
      ) : (
        <div className="form-container">
          <div className="sac-form-header" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`btn btn-sm ${tab === t.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="form-content">
            {tab === 'basic' && (
              <>
                <div className="section-header"><h3>Basic details</h3></div>
                <div className="row g-3">
                  <Field label="Subscription #">
                    <input className="form-control" value={formData.subscriptionNumber || (isCreate ? 'Auto on save' : '')} disabled />
                  </Field>
                  <Field label="Subscription / service name *" className="col-md-8">
                    <input name="subscriptionName" className="form-control" value={formData.subscriptionName} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Description" className="col-12">
                    <textarea name="description" className="form-control" rows={2} value={formData.description} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Vendor / provider">
                    <input name="vendorName" className="form-control" value={formData.vendorName} onChange={handleChange} disabled={disabled} placeholder="GoDaddy, DigitalOcean, Zoho…" />
                  </Field>
                  <Field label="Vendor legal entity">
                    <input name="vendorLegalEntity" className="form-control" value={formData.vendorLegalEntity} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Category">
                    <select name="category" className="form-control" value={formData.category} onChange={handleChange} disabled={disabled}>
                      {SUBSCRIPTION_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Billing model">
                    <select name="billingModel" className="form-control" value={formData.billingModel} onChange={handleChange} disabled={disabled}>
                      {BILLING_MODELS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Plan / package">
                    <input name="planName" className="form-control" value={formData.planName} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Internal reference">
                    <input name="internalReference" className="form-control" value={formData.internalReference} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Vendor account / customer ID">
                    <input name="vendorAccountId" className="form-control" value={formData.vendorAccountId} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Status">
                    <select name="status" className="form-control" value={formData.status} onChange={handleChange} disabled={disabled}>
                      {SUBSCRIPTION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Criticality">
                    <select name="criticality" className="form-control" value={formData.criticality} onChange={handleChange} disabled={disabled}>
                      {CRITICALITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Department">
                    <input name="department" className="form-control" value={formData.department} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Project / product">
                    <input name="projectName" className="form-control" value={formData.projectName} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Cost centre">
                    <input name="costCentre" className="form-control" value={formData.costCentre} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <Field label="Business purpose" className="col-12">
                    <textarea name="businessPurpose" className="form-control" rows={2} value={formData.businessPurpose} onChange={handleChange} disabled={disabled} />
                  </Field>
                </div>
              </>
            )}

            {tab === 'period' && (
              <>
                <div className="section-header"><h3>Subscription period</h3></div>
                <div className="row g-3">
                  <Field label="Start date *"><input type="date" name="startDate" className="form-control" value={formData.startDate} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Current period start"><input type="date" name="currentPeriodStart" className="form-control" value={formData.currentPeriodStart} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Current period end"><input type="date" name="currentPeriodEnd" className="form-control" value={formData.currentPeriodEnd} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Renewal date"><input type="date" name="renewalDate" className="form-control" value={formData.renewalDate} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Expiry date"><input type="date" name="expiryDate" className="form-control" value={formData.expiryDate} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Contract end date"><input type="date" name="contractEndDate" className="form-control" value={formData.contractEndDate} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Notice period (days)"><input type="number" name="noticePeriodDays" className="form-control" value={formData.noticePeriodDays} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Notice deadline"><input type="date" name="noticeDeadline" className="form-control" value={formData.noticeDeadline} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Minimum commitment end"><input type="date" name="minimumCommitmentEnd" className="form-control" value={formData.minimumCommitmentEnd} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Billing frequency">
                    <select name="billingCycle" className="form-control" value={formData.billingCycle} onChange={handleChange} disabled={disabled}>
                      {BILLING_CYCLES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                  <Field label="Grace period (days)"><input type="number" name="gracePeriodDays" className="form-control" value={formData.gracePeriodDays} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Custom billing schedule" className="col-md-8">
                    <input name="customBillingSchedule" className="form-control" value={formData.customBillingSchedule} onChange={handleChange} disabled={disabled} />
                  </Field>
                  <div className="col-md-4 d-flex align-items-end gap-3">
                    <label className="form-check"><input type="checkbox" name="autoRenewal" className="form-check-input" checked={formData.autoRenewal} onChange={handleChange} disabled={disabled} /> Auto-renewal</label>
                    <label className="form-check"><input type="checkbox" name="autoPayment" className="form-check-input" checked={formData.autoPayment} onChange={handleChange} disabled={disabled} /> Auto-payment</label>
                  </div>
                </div>
              </>
            )}

            {tab === 'commercial' && (
              <>
                <div className="section-header"><h3>Commercial details</h3></div>
                <div className="row g-3">
                  <Field label="Currency"><input name="currency" className="form-control" value={formData.currency} onChange={handleChange} disabled={disabled} maxLength={3} /></Field>
                  <Field label="Base recurring amount"><input type="number" name="baseRecurringAmount" className="form-control" value={formData.baseRecurringAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Expected usage amount"><input type="number" name="expectedUsageAmount" className="form-control" value={formData.expectedUsageAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Expected total"><input type="number" name="expectedTotalAmount" className="form-control" value={formData.expectedTotalAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Last billed amount"><input type="number" name="lastBilledAmount" className="form-control" value={formData.lastBilledAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Tax amount"><input type="number" name="taxAmount" className="form-control" value={formData.taxAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Total amount"><input type="number" name="totalAmount" className="form-control" value={formData.totalAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Exchange rate"><input type="number" name="exchangeRate" className="form-control" step="0.0001" value={formData.exchangeRate} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="INR equivalent"><input type="number" name="inrEquivalent" className="form-control" value={formData.inrEquivalent} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Budgeted annual cost"><input type="number" name="budgetedAnnualCost" className="form-control" value={formData.budgetedAnnualCost} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Actual YTD cost"><input type="number" name="actualYtdCost" className="form-control" value={formData.actualYtdCost} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Purchased licences"><input type="number" name="purchasedLicences" className="form-control" value={formData.purchasedLicences} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Assigned licences"><input type="number" name="assignedLicences" className="form-control" value={formData.assignedLicences} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Payment method">
                    <select name="paymentMethod" className="form-control" value={formData.paymentMethod} onChange={handleChange} disabled={disabled}>
                      {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                  <Field label="Card / bank account"><input name="paymentInstrument" className="form-control" value={formData.paymentInstrument} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Payment terms"><input name="paymentTerms" className="form-control" value={formData.paymentTerms} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="PO number"><input name="purchaseOrderNumber" className="form-control" value={formData.purchaseOrderNumber} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Contract number"><input name="contractNumber" className="form-control" value={formData.contractNumber} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Security deposit"><input type="number" name="securityDeposit" className="form-control" value={formData.securityDeposit} onChange={handleChange} disabled={disabled} /></Field>
                  <div className="col-md-4 d-flex align-items-end">
                    <label className="form-check"><input type="checkbox" name="refundableDeposit" className="form-check-input" checked={formData.refundableDeposit} onChange={handleChange} disabled={disabled} /> Refundable deposit</label>
                  </div>
                </div>
                <div className="form-section-divider"><div className="section-header"><h3>Usage budget (variable cloud bills)</h3></div></div>
                <div className="row g-3">
                  <Field label="Estimated monthly"><input type="number" name="usageBudget.estimatedMonthlyCost" className="form-control" value={formData.usageBudget.estimatedMonthlyCost} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Budget limit"><input type="number" name="usageBudget.budgetLimit" className="form-control" value={formData.usageBudget.budgetLimit} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Warning threshold"><input type="number" name="usageBudget.warningThreshold" className="form-control" value={formData.usageBudget.warningThreshold} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Variance % alert"><input type="number" name="usageBudget.variancePercentAlert" className="form-control" value={formData.usageBudget.variancePercentAlert} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Last actual amount"><input type="number" name="usageBudget.lastActualAmount" className="form-control" value={formData.usageBudget.lastActualAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Last actual month"><input name="usageBudget.lastActualMonth" className="form-control" placeholder="YYYY-MM" value={formData.usageBudget.lastActualMonth} onChange={handleChange} disabled={disabled} /></Field>
                </div>
              </>
            )}

            {tab === 'gst' && (
              <>
                <div className="section-header"><h3>GST and accounting</h3></div>
                <p className="text-muted" style={{ fontSize: 13 }}>Links to existing Vendor Invoice / Expense records — does not duplicate GST rules.</p>
                <div className="row g-3">
                  <div className="col-md-4 d-flex flex-column gap-2 justify-content-center">
                    <label className="form-check"><input type="checkbox" name="gst.indianGstApplicable" className="form-check-input" checked={formData.gst.indianGstApplicable} onChange={handleChange} disabled={disabled} /> Indian GST applicable</label>
                    <label className="form-check"><input type="checkbox" name="gst.foreignVendor" className="form-check-input" checked={formData.gst.foreignVendor} onChange={handleChange} disabled={disabled} /> Foreign vendor</label>
                    <label className="form-check"><input type="checkbox" name="gst.importOfServices" className="form-check-input" checked={formData.gst.importOfServices} onChange={handleChange} disabled={disabled} /> Import of services</label>
                    <label className="form-check"><input type="checkbox" name="gst.rcmApplicable" className="form-check-input" checked={formData.gst.rcmApplicable} onChange={handleChange} disabled={disabled} /> RCM applicable</label>
                    <label className="form-check"><input type="checkbox" name="gst.itcEligible" className="form-check-input" checked={formData.gst.itcEligible} onChange={handleChange} disabled={disabled} /> ITC eligible</label>
                  </div>
                  <Field label="Vendor GSTIN"><input name="gst.vendorGstin" className="form-control" value={formData.gst.vendorGstin} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Celeris GSTIN used"><input name="gst.companyGstin" className="form-control" value={formData.gst.companyGstin} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="RCM rate %"><input type="number" name="gst.rcmRate" className="form-control" value={formData.gst.rcmRate} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Expense ledger"><input name="gst.expenseLedger" className="form-control" value={formData.gst.expenseLedger} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Accounting category"><input name="gst.accountingCategory" className="form-control" value={formData.gst.accountingCategory} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="GST verification status">
                    <select name="gst.gstVerificationStatus" className="form-control" value={formData.gst.gstVerificationStatus} onChange={handleChange} disabled={disabled}>
                      {['Not required', 'Pending', 'Verified', 'Disputed'].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Finance / CA remarks" className="col-12">
                    <textarea name="gst.financeRemarks" className="form-control" rows={2} value={formData.gst.financeRemarks} onChange={handleChange} disabled={disabled} />
                  </Field>
                </div>
              </>
            )}

            {tab === 'resources' && (
              <>
                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <h3>Resources under this subscription</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    {resources.length > 0 && (
                      <span className="text-muted" style={{ fontSize: 13 }}>{resourceRangeLabel} · {RESOURCES_PAGE_SIZE} / page</span>
                    )}
                    {resources.length > 0 && (
                      <>
                        <select
                          className="form-control form-control-sm"
                          style={{ width: 'auto', minWidth: 140 }}
                          value={exportResourceType}
                          onChange={(e) => setExportResourceType(e.target.value)}
                          aria-label="Export resource type filter"
                        >
                          <option value="">All types</option>
                          {resourceTypesPresent.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleExportResources}
                          disabled={exportingResources}
                          title="Download full Excel list of resources"
                        >
                          <Download size={14} /> Export Excel
                        </button>
                      </>
                    )}
                    {!disabled && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setFormData((p) => ({ ...p, resources: [...(p.resources || []), emptyResource()] }))
                          setResourcePage(Math.ceil(((formData.resources || []).length + 1) / RESOURCES_PAGE_SIZE))
                        }}
                      >
                        <Plus size={14} /> Add resource
                      </button>
                    )}
                  </div>
                </div>
                {resources.length === 0 && <p className="text-muted">No resources yet. Add domains, droplets, clusters, licences or rental assets.</p>}
                {pagedResources.map(({ resource: r, index: idx }) => (
                  <div key={r._id || `resource-${idx}`} className="card mb-3" style={{ padding: 16, border: '1px solid #E4DFEE' }}>
                    <div className="row g-3">
                      <Field label="Resource name *"><input className="form-control" value={r.name} onChange={(e) => updateResource(idx, { name: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Type">
                        <select className="form-control" value={r.resourceType} onChange={(e) => updateResource(idx, { resourceType: e.target.value })} disabled={disabled}>
                          {RESOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <Field label="Environment">
                        <select className="form-control" value={r.environment || ''} onChange={(e) => updateResource(idx, { environment: e.target.value })} disabled={disabled}>
                          <option value="">—</option>
                          {ENVIRONMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <Field label="Domain name"><input className="form-control" value={r.domainName || ''} onChange={(e) => updateResource(idx, { domainName: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Expiry date"><input type="date" className="form-control" value={r.expiryDate || ''} onChange={(e) => updateResource(idx, { expiryDate: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Renewal date"><input type="date" className="form-control" value={r.renewalDate || ''} onChange={(e) => updateResource(idx, { renewalDate: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Monthly est. cost"><input type="number" className="form-control" value={r.monthlyEstimatedCost || 0} onChange={(e) => updateResource(idx, { monthlyEstimatedCost: parseFloat(e.target.value) || 0 })} disabled={disabled} /></Field>
                      <Field label="Actual latest cost"><input type="number" className="form-control" value={r.actualLatestCost || 0} onChange={(e) => updateResource(idx, { actualLatestCost: parseFloat(e.target.value) || 0 })} disabled={disabled} /></Field>
                      <Field label="Product / project"><input className="form-control" value={r.productOrProject || ''} onChange={(e) => updateResource(idx, { productOrProject: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Asset code (rental)"><input className="form-control" value={r.assetCode || ''} onChange={(e) => updateResource(idx, { assetCode: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Return due date"><input type="date" className="form-control" value={r.returnDueDate || ''} onChange={(e) => updateResource(idx, { returnDueDate: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Purchased licences"><input type="number" className="form-control" value={r.purchasedLicences || 0} onChange={(e) => updateResource(idx, { purchasedLicences: parseFloat(e.target.value) || 0 })} disabled={disabled} /></Field>
                      <Field label="Assigned licences"><input type="number" className="form-control" value={r.assignedLicences || 0} onChange={(e) => updateResource(idx, { assignedLicences: parseFloat(e.target.value) || 0 })} disabled={disabled} /></Field>
                      <Field label="Remarks" className="col-md-8"><input className="form-control" value={r.remarks || ''} onChange={(e) => updateResource(idx, { remarks: e.target.value })} disabled={disabled} /></Field>
                      {!disabled && (
                        <div className="col-md-4 d-flex align-items-end">
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setFormData((p) => ({ ...p, resources: p.resources.filter((_, i) => i !== idx) }))}>
                            <Trash2 size={14} /> Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {resources.length > RESOURCES_PAGE_SIZE && (
                  <div
                    className="d-flex align-items-center justify-content-between"
                    style={{ marginTop: 8, padding: '8px 0', borderTop: '1px solid #E4DFEE' }}
                  >
                    <span className="text-muted" style={{ fontSize: 13 }}>
                      Page {resourcePage} of {resourceTotalPages}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={resourcePage <= 1}
                        onClick={() => setResourcePage((p) => Math.max(1, p - 1))}
                      >
                        <ChevronLeft size={14} /> Previous
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={resourcePage >= resourceTotalPages}
                        onClick={() => setResourcePage((p) => Math.min(resourceTotalPages, p + 1))}
                      >
                        Next <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {tab === 'reminders' && (
              <>
                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Configurable reminders</h3>
                  {!disabled && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFormData((p) => ({ ...p, reminders: [...(p.reminders || []), emptyReminder()] }))}>
                      <Plus size={14} /> Add reminder
                    </button>
                  )}
                </div>
                <p className="text-muted" style={{ fontSize: 13 }}>Schedules are not hard-coded. Changing expiry/renewal dates recalculates dependent triggers automatically.</p>
                {(formData.reminders || []).map((r, idx) => (
                  <div key={r._id || idx} className="card mb-3" style={{ padding: 16, border: '1px solid #E4DFEE' }}>
                    <div className="row g-3">
                      <Field label="Reminder name"><input className="form-control" value={r.name} onChange={(e) => updateReminder(idx, { name: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Trigger based on">
                        <select className="form-control" value={r.triggerBase} onChange={(e) => updateReminder(idx, { triggerBase: e.target.value })} disabled={disabled}>
                          {REMINDER_TRIGGER_BASES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Days offset (negative = before)">
                        <input type="number" className="form-control" value={r.daysOffset} onChange={(e) => updateReminder(idx, { daysOffset: parseInt(e.target.value, 10) || 0 })} disabled={disabled} />
                      </Field>
                      <Field label="Send time"><input type="time" className="form-control" value={r.sendTime || '10:00'} onChange={(e) => updateReminder(idx, { sendTime: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Timezone"><input className="form-control" value={r.timezone || 'Asia/Kolkata'} onChange={(e) => updateReminder(idx, { timezone: e.target.value })} disabled={disabled} /></Field>
                      <Field label="Primary emails (comma-separated)" className="col-md-8">
                        <input
                          className="form-control"
                          value={(r.toEmails || []).join(', ')}
                          onChange={(e) => updateReminder(idx, { toEmails: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                          disabled={disabled}
                        />
                      </Field>
                      <Field label="Subject template" className="col-12">
                        <input className="form-control" value={r.subjectTemplate || ''} onChange={(e) => updateReminder(idx, { subjectTemplate: e.target.value })} disabled={disabled} />
                      </Field>
                      <div className="col-md-6 d-flex gap-3 align-items-center">
                        <label className="form-check"><input type="checkbox" className="form-check-input" checked={r.enabled !== false} onChange={(e) => updateReminder(idx, { enabled: e.target.checked })} disabled={disabled} /> Enabled</label>
                        <label className="form-check"><input type="checkbox" className="form-check-input" checked={r.email !== false} onChange={(e) => updateReminder(idx, { email: e.target.checked })} disabled={disabled} /> Email</label>
                        <label className="form-check"><input type="checkbox" className="form-check-input" checked={r.inApp !== false} onChange={(e) => updateReminder(idx, { inApp: e.target.checked })} disabled={disabled} /> In-app</label>
                      </div>
                      {!disabled && (
                        <div className="col-md-6 d-flex justify-content-end">
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setFormData((p) => ({ ...p, reminders: p.reminders.filter((_, i) => i !== idx) }))}>
                            <Trash2 size={14} /> Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {id && (
                  <div className="page-actions" style={{ marginTop: 12, gap: 8, display: 'flex' }}>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => runReminderAction('preview')} disabled={loading}><Eye size={14} /> Preview</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => runReminderAction('test')} disabled={loading}><Bell size={14} /> Send test</button>
                    <button type="button" className="btn-primary btn-sm" onClick={() => runReminderAction('send')} disabled={loading}><Send size={14} /> Send now</button>
                  </div>
                )}
                {reminderPreview && (
                  <div className="card mt-3" style={{ padding: 16 }}>
                    <h4>Preview</h4>
                    <p><strong>Subject:</strong> {reminderPreview.subject}</p>
                    <p><strong>To:</strong> {(reminderPreview.recipients?.to || []).join(', ') || '—'}</p>
                    <div dangerouslySetInnerHTML={{ __html: reminderPreview.html || '' }} />
                  </div>
                )}
              </>
            )}

            {tab === 'renewal' && (
              <>
                <div className="section-header"><h3>Renewal decision &amp; usage review</h3></div>
                <div className="row g-3">
                  <Field label="Decision">
                    <select name="renewal.decision" className="form-control" value={formData.renewal.decision} onChange={handleChange} disabled={disabled}>
                      {RENEWAL_DECISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Workflow step"><input name="renewal.step" className="form-control" value={formData.renewal.step} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Proposed renewal cost"><input type="number" name="renewal.proposedRenewalCost" className="form-control" value={formData.renewal.proposedRenewalCost} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Requested amount"><input type="number" name="renewal.requestedAmount" className="form-control" value={formData.renewal.requestedAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Approved amount"><input type="number" name="renewal.approvedAmount" className="form-control" value={formData.renewal.approvedAmount} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Approval status">
                    <select name="renewal.approvalStatus" className="form-control" value={formData.renewal.approvalStatus} onChange={handleChange} disabled={disabled}>
                      {['Not required', 'Pending', 'Approved', 'Rejected', 'Changes requested'].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <div className="col-md-4 d-flex align-items-end">
                    <label className="form-check"><input type="checkbox" name="renewal.quotationReceived" className="form-check-input" checked={formData.renewal.quotationReceived} onChange={handleChange} disabled={disabled} /> Quotation received</label>
                  </div>
                  <Field label="Still required?">
                    <select name="usageReview.stillRequired" className="form-control" value={String(formData.usageReview.stillRequired)} onChange={(e) => setFormData((p) => ({ ...p, usageReview: { ...p.usageReview, stillRequired: e.target.value === 'true' } }))} disabled={disabled}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </Field>
                  <Field label="Currently used?">
                    <select name="usageReview.currentlyUsed" className="form-control" value={String(formData.usageReview.currentlyUsed)} onChange={(e) => setFormData((p) => ({ ...p, usageReview: { ...p.usageReview, currentlyUsed: e.target.value === 'true' } }))} disabled={disabled}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </Field>
                  <Field label="Unused licences"><input type="number" name="usageReview.unusedLicences" className="form-control" value={formData.usageReview.unusedLicences} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Recommended action" className="col-md-8"><input name="usageReview.recommendedAction" className="form-control" value={formData.usageReview.recommendedAction} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Estimated savings"><input type="number" name="usageReview.estimatedSavings" className="form-control" value={formData.usageReview.estimatedSavings} onChange={handleChange} disabled={disabled} /></Field>
                  <Field label="Cost optimisation notes" className="col-12">
                    <textarea name="usageReview.costOptimisationOpportunity" className="form-control" rows={2} value={formData.usageReview.costOptimisationOpportunity} onChange={handleChange} disabled={disabled} />
                  </Field>
                </div>
                {id && !isView && (
                  <div className="page-actions" style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={async () => {
                        try {
                          setLoading(true)
                          const res = await subscriptionService.renew(id, { workflowOnly: true, renewal: formData.renewal, decision: formData.renewal.decision })
                          if (res.success) toast.success('Renewal workflow saved')
                          else toast.error(res.message)
                        } catch (e) {
                          if (!e._errorHandled) toast.error('Failed to update renewal')
                        } finally { setLoading(false) }
                      }}
                    >
                      Save renewal decision
                    </button>
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={async () => {
                        try {
                          setLoading(true)
                          const res = await subscriptionService.renew(id, {
                            decision: formData.renewal.decision,
                            proposedRenewalCost: formData.renewal.proposedRenewalCost,
                          })
                          if (res.success) {
                            toast.success('Renewal completed')
                            loadSubscription(id)
                          } else toast.error(res.message)
                        } catch (e) {
                          if (!e._errorHandled) toast.error('Renewal failed')
                        } finally { setLoading(false) }
                      }}
                    >
                      Complete renewal
                    </button>
                  </div>
                )}
              </>
            )}

            {tab === 'documents' && (
              <>
                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <h3>Documents</h3>
                  {!disabled && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setFormData((p) => ({
                        ...p,
                        documents: [...(p.documents || []), { documentType: 'Other', fileName: '', fileUrl: '', notes: '' }],
                      }))}
                    >
                      <Plus size={14} /> Add document link
                    </button>
                  )}
                </div>
                <p className="text-muted" style={{ fontSize: 13 }}>
                  Uses existing ERP upload (`/api/upload/document`) or a safe HTTPS / `/api/documents/` reference.
                </p>
                {!disabled && (
                  <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <label className="btn btn-sm btn-outline-secondary" style={{ marginBottom: 0, cursor: 'pointer' }}>
                      Upload file
                      <input
                        type="file"
                        hidden
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          try {
                            setLoading(true)
                            const res = await fileUploadService.uploadFile(file, 'subscription')
                            const data = res.data || res
                            if (res.success !== false && data) {
                              setFormData((p) => ({
                                ...p,
                                documents: [...(p.documents || []), {
                                  documentType: 'Other',
                                  fileName: data.originalname || file.name,
                                  originalName: data.originalname || file.name,
                                  filePath: data.filename,
                                  fileUrl: data.url || (data.filename ? `/api/documents/${data.filename}` : ''),
                                  mimeType: data.mimetype || file.type,
                                  size: data.size || file.size,
                                  status: 'Active',
                                }],
                              }))
                              toast.success('Document uploaded')
                            } else toast.error(res.message || 'Upload failed')
                          } catch (err) {
                            if (!err._errorHandled) toast.error('Upload failed')
                          } finally {
                            setLoading(false)
                            e.target.value = ''
                          }
                        }}
                      />
                    </label>
                  </div>
                )}
                {(formData.documents || []).map((d, idx) => (
                  <div key={idx} className="row g-3 mb-2 align-items-end">
                    <Field label="Type">
                      <select className="form-control" value={d.documentType} onChange={(e) => {
                        const documents = [...formData.documents]
                        documents[idx] = { ...documents[idx], documentType: e.target.value }
                        setFormData((p) => ({ ...p, documents }))
                      }} disabled={disabled}>
                        {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </Field>
                    <Field label="File name"><input className="form-control" value={d.fileName || ''} onChange={(e) => {
                      const documents = [...formData.documents]
                      documents[idx] = { ...documents[idx], fileName: e.target.value }
                      setFormData((p) => ({ ...p, documents }))
                    }} disabled={disabled} /></Field>
                    <Field label="URL / path" className="col-md-4"><input className="form-control" value={d.fileUrl || ''} onChange={(e) => {
                      const documents = [...formData.documents]
                      documents[idx] = { ...documents[idx], fileUrl: e.target.value }
                      setFormData((p) => ({ ...p, documents }))
                    }} disabled={disabled} /></Field>
                  </div>
                ))}
                <div className="form-section-divider"><div className="section-header"><h3>Notes</h3></div></div>
                <textarea name="notes" className="form-control" rows={3} value={formData.notes} onChange={handleChange} disabled={disabled} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionForm
